import { computed, inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { ENV_CONFIG, WINDOW } from '@ps/core-config';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import { PS_CATCH_ERROR } from '@ps/utils';

import { PlatformSwitcherApi } from '../api/platform-switcher.api';
import { SwitchOutcome, SwitchToLegacyRequest } from '../models/switch.model';
import { toLegacyPath } from '../routes/platform-switcher.config';

/** URL fragment key the handover token travels under, never a query param. See implementation_plan.md §2/Q7. */
const HANDOVER_FRAGMENT_KEY = 'ht';

@Injectable({ providedIn: 'root' })
export class PlatformSwitcherService {
  private readonly _api = inject(PlatformSwitcherApi);
  private readonly _auth = inject(AuthSelectorsFacade);
  private readonly _userSettings = inject(UserSettingsSelectorsFacade);
  private readonly _env = inject(ENV_CONFIG);
  private readonly _window = inject(WINDOW);
  private readonly _psCatchError = inject(PS_CATCH_ERROR);

  /** Buyer role (server-enforced too, see `HasRole(['buyer'])` on the handover-token endpoint) AND the `enablePlatformSwitcher` per-buyer flag. */
  public readonly canSwitch = computed(
    () =>
      this._auth.isBuyer() &&
      !!this._userSettings.buyerSettings().enablePlatformSwitcher,
  );

  /**
   * Requests a handover token and redirects to the equivalent Legacy route.
   * Never opens a dialog itself, an unsaved-changes conflict is reported
   * as `confirmationRequired` for the caller's own warning dialog (T5) to
   * resolve, then re-invoke with `force: true`.
   */
  public switchToLegacy(
    request: SwitchToLegacyRequest,
  ): Observable<SwitchOutcome> {
    if (!this.canSwitch()) {
      return of<SwitchOutcome>('blocked');
    }

    if (request.hasUnsavedChanges && !request.force) {
      return of<SwitchOutcome>('confirmationRequired');
    }

    return this._api.createHandoverToken().pipe(
      map((dto): SwitchOutcome => {
        this._redirectToLegacy(request, dto.handoverToken);
        return 'switched';
      }),
      this._psCatchError(() => of<SwitchOutcome>('error'), {
        notify: true,
        customMessage:
          'Unable to switch to Legacy UI right now. Please try again.',
      }),
    );
  }

  private _redirectToLegacy(
    request: SwitchToLegacyRequest,
    handoverToken: string,
  ): void {
    const path = toLegacyPath(request.routeKey, request.params);
    const legacyOrigin = this._env.pureSpectrum.legacyPlatformUrl;

    this._window.location.href = `${legacyOrigin}${path}#${HANDOVER_FRAGMENT_KEY}=${handoverToken}`;
  }
}
